/**
 * Image Cache Manager
 * 
 * Manages client-side caching of item icons and other images using IndexedDB.
 * Reduces CDN bandwidth usage by storing images as blobs locally.
 * 
 * Features:
 * - IndexedDB storage for persistent caching
 * - TTL-based expiration (default: 7 days)
 * - Size limits with LRU eviction
 * - Preloading for common icons
 * - Cache statistics and monitoring
 * - Automatic cleanup of expired entries
 */

interface CachedImage {
  url: string;
  blob: Blob;
  timestamp: number;
  accessCount: number;
  lastAccessed: number;
  size: number;
}

interface ImageCacheStats {
  totalImages: number;
  totalSize: number;
  hits: number;
  misses: number;
  hitRate: number;
  oldestEntry: number | null;
  newestEntry: number | null;
}

export class ImageCacheManager {
  private static instance: ImageCacheManager;
  private db: IDBDatabase | null = null;
  private dbName = 'grandline_image_cache';
  private storeName = 'images';
  private version = 1;
  
  // Cache configuration
  private maxCacheSize = 50 * 1024 * 1024; // 50MB max cache size
  private maxEntries = 500; // Maximum number of cached images
  private defaultTTL = 7 * 24 * 60 * 60 * 1000; // 7 days
  
  // Statistics
  private stats = {
    hits: 0,
    misses: 0,
  };
  
  // In-memory cache for faster access to recently used images
  private memoryCache = new Map<string, string>(); // url -> blob URL
  private memoryCacheSize = 0;
  private maxMemoryCacheSize = 10 * 1024 * 1024; // 10MB in memory
  
  private constructor() {
    this.initDB();
  }

  public static getInstance(): ImageCacheManager {
    if (!ImageCacheManager.instance) {
      ImageCacheManager.instance = new ImageCacheManager();
    }
    return ImageCacheManager.instance;
  }

  /**
   * Initialize IndexedDB
   */
  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => {
        console.error('Failed to open ImageCache DB:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ ImageCache DB initialized');
        this.cleanupExpired(); // Clean up on init
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          const objectStore = db.createObjectStore(this.storeName, { keyPath: 'url' });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('lastAccessed', 'lastAccessed', { unique: false });
          objectStore.createIndex('size', 'size', { unique: false });
          console.log('📦 Created ImageCache object store');
        }
      };
    });
  }

  /**
   * Ensure DB is ready
   */
  private async ensureDB(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    
    await this.initDB();
    if (!this.db) {
      throw new Error('Failed to initialize ImageCache DB');
    }
    return this.db;
  }

  /**
   * Get image from cache
   */
  public async get(url: string): Promise<string | null> {
    // Check memory cache first
    const memoryCached = this.memoryCache.get(url);
    if (memoryCached) {
      this.stats.hits++;
      return memoryCached;
    }

    try {
      const db = await this.ensureDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise((resolve) => {
        const request = store.get(url);
        
        request.onsuccess = () => {
          const cached = request.result as CachedImage | undefined;
          
          if (!cached) {
            this.stats.misses++;
            resolve(null);
            return;
          }
          
          // Check if expired
          const now = Date.now();
          if (now - cached.timestamp > this.defaultTTL) {
            this.stats.misses++;
            store.delete(url); // Clean up expired entry
            resolve(null);
            return;
          }
          
          // Update access stats
          cached.accessCount++;
          cached.lastAccessed = now;
          store.put(cached);
          
          // Create blob URL and cache in memory
          const blobUrl = URL.createObjectURL(cached.blob);
          this.addToMemoryCache(url, blobUrl, cached.size);
          
          this.stats.hits++;
          resolve(blobUrl);
        };
        
        request.onerror = () => {
          console.error('Failed to get image from cache:', request.error);
          this.stats.misses++;
          resolve(null);
        };
      });
    } catch (error) {
      console.error('Error accessing image cache:', error);
      this.stats.misses++;
      return null;
    }
  }

  /**
   * Store image in cache
   */
  public async set(url: string, blob: Blob): Promise<boolean> {
    try {
      const db = await this.ensureDB();
      
      // Check cache size limits before adding
      const stats = await this.getStats();
      if (stats.totalSize + blob.size > this.maxCacheSize || stats.totalImages >= this.maxEntries) {
        await this.evictLRU();
      }
      
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      const cached: CachedImage = {
        url,
        blob,
        timestamp: Date.now(),
        accessCount: 1,
        lastAccessed: Date.now(),
        size: blob.size,
      };
      
      return new Promise((resolve) => {
        const request = store.put(cached);
        
        request.onsuccess = () => {
          // Add to memory cache
          const blobUrl = URL.createObjectURL(blob);
          this.addToMemoryCache(url, blobUrl, blob.size);
          resolve(true);
        };
        
        request.onerror = () => {
          console.error('Failed to cache image:', request.error);
          resolve(false);
        };
      });
    } catch (error) {
      console.error('Error caching image:', error);
      return false;
    }
  }

  /**
   * Add to memory cache with size limit
   */
  private addToMemoryCache(url: string, blobUrl: string, size: number): void {
    // Evict from memory cache if needed
    while (this.memoryCacheSize + size > this.maxMemoryCacheSize && this.memoryCache.size > 0) {
      const firstKey = this.memoryCache.keys().next().value;
      const firstUrl = this.memoryCache.get(firstKey);
      if (firstUrl) {
        URL.revokeObjectURL(firstUrl);
      }
      this.memoryCache.delete(firstKey);
      this.memoryCacheSize -= size; // Approximate
    }
    
    this.memoryCache.set(url, blobUrl);
    this.memoryCacheSize += size;
  }

  /**
   * Fetch and cache an image
   */
  public async fetchAndCache(url: string): Promise<string | null> {
    // Check cache first
    const cached = await this.get(url);
    if (cached) return cached;
    
    // Fetch from network
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.warn(`Failed to fetch image: ${url} (${response.status})`);
        return null;
      }
      
      const blob = await response.blob();
      await this.set(url, blob);
      
      // Return blob URL
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error fetching image:', error);
      return null;
    }
  }

  /**
   * Preload multiple images
   */
  public async preload(urls: string[]): Promise<void> {
    const promises = urls.map(url => this.fetchAndCache(url));
    await Promise.allSettled(promises);
  }

  /**
   * Evict least recently used entries
   */
  private async evictLRU(): Promise<void> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('lastAccessed');
      
      return new Promise((resolve) => {
        const request = index.openCursor();
        let evicted = 0;
        const evictCount = Math.max(10, Math.floor(this.maxEntries * 0.1)); // Evict 10% or 10 entries
        
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor && evicted < evictCount) {
            cursor.delete();
            evicted++;
            cursor.continue();
          } else {
            console.log(`🗑️ Evicted ${evicted} images from cache`);
            resolve();
          }
        };
        
        request.onerror = () => {
          console.error('Failed to evict LRU entries:', request.error);
          resolve();
        };
      });
    } catch (error) {
      console.error('Error evicting LRU entries:', error);
    }
  }

  /**
   * Clean up expired entries
   */
  public async cleanupExpired(): Promise<number> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise((resolve) => {
        const request = store.openCursor();
        let cleaned = 0;
        const now = Date.now();
        
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            const cached = cursor.value as CachedImage;
            if (now - cached.timestamp > this.defaultTTL) {
              cursor.delete();
              cleaned++;
            }
            cursor.continue();
          } else {
            if (cleaned > 0) {
              console.log(`🧹 Cleaned up ${cleaned} expired images`);
            }
            resolve(cleaned);
          }
        };
        
        request.onerror = () => {
          console.error('Failed to cleanup expired entries:', request.error);
          resolve(0);
        };
      });
    } catch (error) {
      console.error('Error cleaning up expired entries:', error);
      return 0;
    }
  }

  /**
   * Get cache statistics
   */
  public async getStats(): Promise<ImageCacheStats> {
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise((resolve) => {
        const request = store.openCursor();
        let totalImages = 0;
        let totalSize = 0;
        let oldestEntry: number | null = null;
        let newestEntry: number | null = null;
        
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            const cached = cursor.value as CachedImage;
            totalImages++;
            totalSize += cached.size;
            
            if (oldestEntry === null || cached.timestamp < oldestEntry) {
              oldestEntry = cached.timestamp;
            }
            if (newestEntry === null || cached.timestamp > newestEntry) {
              newestEntry = cached.timestamp;
            }
            
            cursor.continue();
          } else {
            const hitRate = this.stats.hits + this.stats.misses > 0
              ? this.stats.hits / (this.stats.hits + this.stats.misses)
              : 0;
            
            resolve({
              totalImages,
              totalSize,
              hits: this.stats.hits,
              misses: this.stats.misses,
              hitRate,
              oldestEntry,
              newestEntry,
            });
          }
        };
        
        request.onerror = () => {
          console.error('Failed to get stats:', request.error);
          resolve({
            totalImages: 0,
            totalSize: 0,
            hits: this.stats.hits,
            misses: this.stats.misses,
            hitRate: 0,
            oldestEntry: null,
            newestEntry: null,
          });
        };
      });
    } catch (error) {
      console.error('Error getting stats:', error);
      return {
        totalImages: 0,
        totalSize: 0,
        hits: this.stats.hits,
        misses: this.stats.misses,
        hitRate: 0,
        oldestEntry: null,
        newestEntry: null,
      };
    }
  }

  /**
   * Clear all cached images
   */
  public async clear(): Promise<void> {
    try {
      // Clear memory cache
      this.memoryCache.forEach(blobUrl => URL.revokeObjectURL(blobUrl));
      this.memoryCache.clear();
      this.memoryCacheSize = 0;
      
      // Clear IndexedDB
      const db = await this.ensureDB();
      const transaction = db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise((resolve) => {
        const request = store.clear();
        
        request.onsuccess = () => {
          console.log('🗑️ Image cache cleared');
          this.stats.hits = 0;
          this.stats.misses = 0;
          resolve();
        };
        
        request.onerror = () => {
          console.error('Failed to clear cache:', request.error);
          resolve();
        };
      });
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  }

  /**
   * Check if image is cached
   */
  public async has(url: string): Promise<boolean> {
    if (this.memoryCache.has(url)) return true;
    
    try {
      const db = await this.ensureDB();
      const transaction = db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      
      return new Promise((resolve) => {
        const request = store.get(url);
        
        request.onsuccess = () => {
          const cached = request.result as CachedImage | undefined;
          if (!cached) {
            resolve(false);
            return;
          }
          
          // Check if expired
          const now = Date.now();
          resolve(now - cached.timestamp <= this.defaultTTL);
        };
        
        request.onerror = () => {
          resolve(false);
        };
      });
    } catch (error) {
      return false;
    }
  }

  /**
   * Get formatted stats for display
   */
  public async getFormattedStats(): Promise<string> {
    const stats = await this.getStats();
    const sizeMB = (stats.totalSize / (1024 * 1024)).toFixed(2);
    const hitRatePercent = (stats.hitRate * 100).toFixed(1);
    
    return `
📊 Image Cache Statistics:
  Images: ${stats.totalImages}
  Size: ${sizeMB} MB / ${(this.maxCacheSize / (1024 * 1024)).toFixed(0)} MB
  Hit Rate: ${hitRatePercent}% (${stats.hits} hits, ${stats.misses} misses)
  Memory Cache: ${this.memoryCache.size} images
  Oldest: ${stats.oldestEntry ? new Date(stats.oldestEntry).toLocaleString() : 'N/A'}
  Newest: ${stats.newestEntry ? new Date(stats.newestEntry).toLocaleString() : 'N/A'}
    `.trim();
  }

  /**
   * Destroy and cleanup
   */
  public destroy(): void {
    // Revoke all blob URLs
    this.memoryCache.forEach(blobUrl => URL.revokeObjectURL(blobUrl));
    this.memoryCache.clear();
    
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}

// Export singleton instance
const imageCacheManager = ImageCacheManager.getInstance();
export default imageCacheManager;






